import { useCallback, useRef, useState } from "react";
import type { ChangeEvent, SyntheticEvent } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { HashIcon, PlusIcon, Trash2Icon, UploadIcon } from "lucide-react";

import { countries, flagEmoji, regions } from "simplydo/core/countries";
import {
  accreditations as accreditationOptions,
  businessSizeOptions,
  businessStatusOptions,
  businessTypeOptions,
  computeBusinessStatus,
  isCompanyNumberTooLong,
} from "simplydo/core/ideaBusinessProfiles";
import { asMutation, initUseQuery } from "simplydo/core/query";
import { useThrottle } from "simplydo/react/hooks";
import { css, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import type { DropdownProps } from "simplydo/react/ui/dropdown";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import type { InputProps } from "simplydo/react/ui/input";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import { TextArea } from "simplydo/react/ui/textarea";
import type { TextAreaProps } from "simplydo/react/ui/textarea";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { ImageChooser } from "../../Choosers";
import TagModalChooser from "../../Choosers/Tags/TagModalChooser";
import type { IdeaBusinessProfile, IdeaBusinessProfileUpdate } from "../types";
import {
  CompaniesHouseCheckCard,
  VatNumberCheckCard,
} from "./IdentifierCheckCards";
import { IdentifierLookupStatus } from "./IdentifierLookupStatus";
import {
  isLikelyCompanyNumber,
  isLikelyVatNumber,
  isVatNumberTooLong,
  normaliseVatNumber,
  pickVatMatch,
  type IdentifierLookupState,
} from "./identifierLookup";

const uploadImageSegmentClass = css`
  min-width: 100%;
  max-width: 200px;
  margin-bottom: 16px !important;

  .ui.image {
    &:hover {
      cursor: pointer;
      opacity: 0.8;
    }
  }
`;

const AccreditationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  .ui.label {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

type EditOverviewProps = {
  businessProfile: IdeaBusinessProfile;
  setUnsaved?: (unsaved: boolean) => void;
};

export const EditOverview = ({
  businessProfile,
  setUnsaved,
}: EditOverviewProps) => {
  const theme = useTheme();
  const [addingCustomAccreditation, setAddingCustomAccreditation] =
    useState(false);
  const [customAccreditation, setCustomAccreditation] = useState("");
  const user = useCurrentUser();
  const { t } = useTranslation();

  const {
    name,
    description,
    address = {},
    websiteUrl,
    websiteUrlSkipped,
    size,
    type,
    accreditations: profileAccreditations = [],
    foundingYear,
    creditsafeRegNo,
    creditsafeRegNoSkipped,
    vatNo,
    vatNoSkipped,
    ownerTags,
  } = businessProfile || {};
  const { country, city, streetAddress, postcode, region } = address;
  const tagsQuery = useQuery(
    initUseQuery(
      api.tags.getAllForEntity,
      ["organisation", user?.organisation ?? "", false],
      { isEnabled: !!user?.organisation },
    ),
  );

  const addTagMutation = useMutation({
    mutationKey: ["post", "tags", "ideaBusinessProfiles", "tag"],
    mutationFn: asMutation(api.tags.tagSingle)!,
    onSuccess: (tagData) => {
      api.setQueryData(
        api.users.getProfile,
        [businessProfile._id],
        (prev) =>
          ({
            ideaBusinessProfile: {
              ...prev.ideaBusinessProfile,
              tags: [...(prev.ideaBusinessProfile?.tags ?? []), tagData._id],
              ownerTags: [
                ...(prev.ideaBusinessProfile?.ownerTags ?? []),
                tagData,
              ],
            },
          }) as typeof prev,
      );
    },
  });

  const removeTagMutation = useMutation({
    mutationKey: ["delete", "tags", "ideaBusinessProfiles", "tag"],
    mutationFn: asMutation(api.tags.untagSingle)!,
    onSuccess: (tagData) => {
      api.setQueryData(api.users.getProfile, [businessProfile._id], (prev) => ({
        ideaBusinessProfile: {
          ...prev.ideaBusinessProfile,
          tags: prev.ideaBusinessProfile?.tags?.filter(
            (t) => t !== tagData._id,
          ),
          ownerTags: prev.ideaBusinessProfile?.ownerTags?.filter(
            (t) => t._id !== tagData._id,
          ),
        },
      }));
    },
  });

  const updateProfile = useCallback(
    (data: IdeaBusinessProfileUpdate) => {
      api.setQueryData(
        api.users.getProfile,
        [businessProfile._id],
        (prev) =>
          ({
            ideaBusinessProfile: { ...prev.ideaBusinessProfile, ...data },
          }) as typeof prev,
      );

      setUnsaved?.(true);
    },
    [setUnsaved, businessProfile],
  );

  const [regLookup, setRegLookup] = useState<IdentifierLookupState>({
    status: "idle",
  });
  const [vatLookup, setVatLookup] = useState<IdentifierLookupState>({
    status: "idle",
  });
  const [vatSuggestion, setVatSuggestion] = useState<{
    vatNo: string;
    name: string;
  } | null>(null);
  const regLookupValueRef = useRef("");
  const vatLookupValueRef = useRef("");
  const [savedRegNo] = useState(creditsafeRegNo ?? "");
  const [savedVatNo] = useState(vatNo ?? "");
  const regIsEditing = (creditsafeRegNo ?? "") !== savedRegNo;
  const vatIsEditing = (vatNo ?? "") !== savedVatNo;

  const lookupCompaniesHouse = useThrottle(
    (companyNumber: string) => {
      if (regLookupValueRef.current !== companyNumber) {
        return;
      }
      if (!isLikelyCompanyNumber(companyNumber)) {
        setRegLookup({ status: "idle" });
        return;
      }
      setRegLookup({ status: "pending" });
      api.verifications.lookupCompaniesHouse(
        companyNumber,
        (data) => {
          if (regLookupValueRef.current !== companyNumber) {
            return;
          }
          const resolvedName = data.company?.company_name;
          if (data.isAvailable && resolvedName) {
            setRegLookup({ status: "found", name: resolvedName });
          } else {
            setRegLookup({ status: "notFound" });
          }
        },
        () => {
          if (regLookupValueRef.current !== companyNumber) {
            return;
          }
          setRegLookup({ status: "notFound" });
        },
      );
    },
    400,
    [],
  );

  const lookupCreditsafeVat = useThrottle(
    (
      vatNumber: string,
      countryCode: string,
      profileRegNo: string,
      profileName: string,
    ) => {
      if (vatLookupValueRef.current !== vatNumber) {
        return;
      }
      if (!isLikelyVatNumber(vatNumber) || !user?.organisation) {
        setVatLookup({ status: "idle" });
        return;
      }
      setVatLookup({ status: "pending" });
      api.verifications.searchForCompanies(
        countryCode || "GB",
        "",
        "",
        "org",
        user.organisation,
        (data) => {
          if (vatLookupValueRef.current !== vatNumber) {
            return;
          }
          const companies = data.companies ?? [];
          const match = pickVatMatch(companies, profileRegNo, profileName);
          if (match?.name) {
            setVatLookup({ status: "found", name: match.name });
          } else if (companies.length > 1) {
            setVatLookup({
              status: "vatGroup",
              companies: companies.map(({ name, regNo }) => ({ name, regNo })),
              hasRegNo: !!profileRegNo.trim(),
            });
          } else {
            setVatLookup({ status: "notFound" });
          }
        },
        () => {
          if (vatLookupValueRef.current !== vatNumber) {
            return;
          }
          setVatLookup({ status: "notFound" });
        },
        normaliseVatNumber(vatNumber),
      );
    },
    400,
    [user?.organisation],
  );

  const lookupVatSuggestion = useThrottle(
    (companyNumber: string, countryCode: string) => {
      if (regLookupValueRef.current !== companyNumber || !user?.organisation) {
        return;
      }
      api.verifications.searchForCompanies(
        countryCode || "GB",
        companyNumber,
        "",
        "org",
        user.organisation,
        (data) => {
          if (regLookupValueRef.current !== companyNumber) {
            return;
          }
          const match = pickVatMatch(data.companies ?? [], companyNumber);
          const suggestedVatNo = match?.vatNo?.[0];
          setVatSuggestion(
            suggestedVatNo
              ? { vatNo: suggestedVatNo, name: match.name ?? "" }
              : null,
          );
        },
        () => {},
      );
    },
    400,
    [user?.organisation],
  );

  const onRegNoChange = useCallback(
    (_e: SyntheticEvent, { value }: InputProps) => {
      const nextValue = String(value ?? "");
      updateProfile({ creditsafeRegNo: nextValue });
      regLookupValueRef.current = nextValue;
      setVatSuggestion(null);
      if (nextValue === savedRegNo || creditsafeRegNoSkipped) {
        setRegLookup({ status: "idle" });
        return;
      }
      if (isCompanyNumberTooLong(nextValue)) {
        setRegLookup({ status: "companyNumberTooLong" });
        return;
      }
      if (!isLikelyCompanyNumber(nextValue)) {
        setRegLookup({ status: "idle" });
        return;
      }
      setRegLookup({ status: "pending" });
      lookupCompaniesHouse(nextValue);
      if (!vatNo && !vatNoSkipped) {
        lookupVatSuggestion(nextValue, country || "GB");
      }
    },
    [
      country,
      creditsafeRegNoSkipped,
      lookupCompaniesHouse,
      lookupVatSuggestion,
      savedRegNo,
      updateProfile,
      vatNo,
      vatNoSkipped,
    ],
  );

  const applyVatSuggestion = () => {
    if (!vatSuggestion) {
      return;
    }
    updateProfile({ vatNo: vatSuggestion.vatNo });
    vatLookupValueRef.current = vatSuggestion.vatNo;
    setVatLookup({ status: "found", name: vatSuggestion.name });
    setVatSuggestion(null);
  };

  const onVatNoChange = useCallback(
    (_e: SyntheticEvent, { value }: InputProps) => {
      const nextValue = String(value ?? "");
      updateProfile({ vatNo: nextValue });
      vatLookupValueRef.current = nextValue;
      setVatSuggestion(null);
      if (nextValue === savedVatNo || vatNoSkipped) {
        setVatLookup({ status: "idle" });
        return;
      }
      if (isVatNumberTooLong(nextValue)) {
        setVatLookup({ status: "vatTooLong" });
        return;
      }
      if (!isLikelyVatNumber(nextValue)) {
        setVatLookup({ status: "idle" });
        return;
      }
      setVatLookup({ status: "pending" });
      lookupCreditsafeVat(
        nextValue,
        country || "GB",
        creditsafeRegNo ?? "",
        name ?? "",
      );
    },
    [
      country,
      creditsafeRegNo,
      lookupCreditsafeVat,
      name,
      savedVatNo,
      updateProfile,
      vatNoSkipped,
    ],
  );

  const currentYear = new Date().getFullYear();
  // Status is derived from the founding year and only locked in when an idea is submitted
  const status = computeBusinessStatus(foundingYear);

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 24,
          flexDirection: theme.sizes.isComputer ? "row" : "column",
        }}
      >
        <ImageChooser
          forType="ideaBusinessProfile"
          forId={businessProfile._id}
          trigger={
            <div>
              <Card fluid className={uploadImageSegmentClass}>
                <CardContent>
                  {businessProfile?.imageUrl ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignSelf: "flex-start",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 4,
                          overflow: "hidden",
                          maxHeight: 150,
                          width: 150,
                        }}
                      >
                        <Image
                          style={{ objectFit: "contain" }}
                          src={businessProfile?.imageUrl}
                          alt={businessProfile?.name}
                        />
                      </div>
                      <Button
                        size="small"
                        fluid
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          util
                            .confirm(
                              "Remove logo",
                              "Are you sure you want to remove the logo?",
                            )
                            .then(() => {
                              updateProfile({ imageUrl: "", image: "" });
                            })
                            .catch(() => {});
                        }}
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Message color="blue">
                        <MessageHeader>Upload a logo</MessageHeader>
                        <p>
                          We recommend a square image with a size of 500x500
                          pixels in either PNG or JPG format.
                        </p>
                      </Message>
                      <Button fluid>
                        <Icon icon={UploadIcon} />
                        Choose image
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          }
          onComplete={(responseFilename, _fn, downloadUrl) => {
            updateProfile({ imageUrl: downloadUrl, image: responseFilename });
          }}
        />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <FormField required>
            <FormLabel>Organisation name</FormLabel>
            <Input
              fluid
              placeholder="Type your organisation name.."
              defaultValue={name}
              onChange={(_e: SyntheticEvent, { value }: InputProps) => {
                updateProfile({ name: String(value ?? "") });
              }}
            />
          </FormField>
          <Grid columns={2} mobile={1}>
            <GridColumn>
              <FormField required>
                <FormLabel>Phone number</FormLabel>
                <Input
                  fluid
                  placeholder="+44 0000 000000"
                  defaultValue={businessProfile.phoneNumber}
                  onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                    updateProfile({ phoneNumber: String(value ?? "") })
                  }
                />
              </FormField>
            </GridColumn>
            <GridColumn>
              <FormField required>
                <FormLabel>Your business website</FormLabel>
                <Input
                  fluid
                  placeholder="https://www.example.com"
                  disabled={websiteUrlSkipped}
                  defaultValue={websiteUrl}
                  onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                    updateProfile({ websiteUrl: String(value ?? "") })
                  }
                />
              </FormField>
              <Checkbox
                style={{ marginTop: "0.3rem" }}
                onChange={(_e, { checked }) =>
                  updateProfile({ websiteUrlSkipped: checked })
                }
                checked={websiteUrlSkipped}
              >
                My business does not have a website yet
              </Checkbox>
            </GridColumn>
          </Grid>
        </div>
      </div>
      <FormField>
        <FormLabel>About the organisation</FormLabel>
        <TextArea
          defaultValue={description}
          onChange={(_e: SyntheticEvent, { value }: TextAreaProps) =>
            updateProfile({ description: String(value ?? "") })
          }
        />
      </FormField>
      <H4 style={{ marginBottom: 0 }}>Registered address</H4>
      <p
        style={{ color: "grey", marginTop: 5 }}
      >{`This must be your official registered address as recorded with Companies House (or other official register).  If you are a sole trader or another type of organisation, use your trading address or an address where your business can be reached.`}</p>
      <FormField required>
        <FormLabel>Street address</FormLabel>
        <Input
          fluid
          defaultValue={streetAddress}
          onChange={(_e: SyntheticEvent, { value }: InputProps) =>
            updateProfile({
              address: {
                ...(address || {}),
                streetAddress: String(value ?? ""),
              },
            })
          }
        />
      </FormField>
      <Grid
        columns={
          country === "GB" ||
          country === "England" ||
          country === "United Kingdom"
            ? 2
            : 1
        }
        mobile={1}
      >
        <GridColumn>
          <FormField required>
            <FormLabel>Country</FormLabel>
            <Dropdown
              variant="outline"
              search
              clearable
              value={country}
              onChange={(_e: SyntheticEvent, { value }: DropdownProps) => {
                updateProfile({
                  address: {
                    ...(address || {}),
                    country: value as string,
                    region:
                      value !== "GB" ? "" : businessProfile?.address?.region,
                  },
                });
              }}
              placeholder="Select one"
              options={countries.map((c) => ({
                key: c.iso2,
                value: c.iso2,
                text: `${flagEmoji(c.iso2)} ${c.name}`,
              }))}
            />
          </FormField>
        </GridColumn>
        {country === "GB" ||
        country === "England" ||
        country === "United Kingdom" ? (
          <GridColumn>
            <FormField required>
              <FormLabel>Region</FormLabel>
              <Dropdown
                variant="outline"
                search
                clearable
                value={region}
                onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
                  updateProfile({
                    address: { ...(address || {}), region: value as string },
                  })
                }
                options={regions.map((r) => ({
                  key: r.value,
                  value: r.value,
                  text: r.name,
                }))}
              />
            </FormField>
          </GridColumn>
        ) : null}
      </Grid>
      <Grid columns={2} mobile={1}>
        <GridColumn>
          <FormField required>
            <FormLabel>City</FormLabel>
            <Input
              fluid
              defaultValue={city}
              onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                updateProfile({
                  address: { ...(address || {}), city: String(value ?? "") },
                })
              }
            />
          </FormField>
        </GridColumn>
        <GridColumn>
          <FormField required>
            <FormLabel>Postcode</FormLabel>
            <Input
              fluid
              defaultValue={postcode}
              onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                updateProfile({
                  address: {
                    ...(address || {}),
                    postcode: String(value ?? ""),
                  },
                })
              }
            />
          </FormField>
        </GridColumn>
      </Grid>

      <Divider hidden />
      <H4>Additional information</H4>
      <Grid columns={2} mobile={1}>
        <GridColumn>
          <FormField required>
            <FormLabel>Organisation size</FormLabel>
            <Dropdown
              variant="outline"
              value={size}
              options={businessSizeOptions}
              onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
                updateProfile({
                  size: value as "micro" | "small" | "medium" | "large",
                })
              }
            />
          </FormField>
        </GridColumn>
        <GridColumn>
          <FormField required>
            <FormLabel>Organisation type</FormLabel>
            <Dropdown
              variant="outline"
              value={type}
              options={businessTypeOptions}
              onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
                updateProfile({
                  type: value as
                    | "public"
                    | "private"
                    | "notForProfit"
                    | "academic",
                })
              }
            />
          </FormField>
        </GridColumn>
      </Grid>
      <div>
        <Grid columns={2} mobile={1}>
          <GridColumn>
            <FormField
              required
              error={
                foundingYear
                  ? foundingYear < 1000 || foundingYear > currentYear
                  : undefined
              }
            >
              <FormLabel>Organisation founding year</FormLabel>
              <Input
                fluid
                type="number"
                placeholder="2011"
                defaultValue={foundingYear ?? undefined}
                onChange={(_e: SyntheticEvent, { value }: InputProps) => {
                  const year = parseInt(String(value ?? ""), 10);
                  updateProfile({
                    foundingYear: Number.isNaN(year) ? null : year,
                  });
                }}
              />
            </FormField>
          </GridColumn>
          <GridColumn>
            <FormField>
              <FormLabel>Organisation status</FormLabel>
              <div
                style={{ height: 36, display: "flex", alignItems: "center" }}
              >
                <i>
                  {status
                    ? (businessStatusOptions.find(
                        (statusOption) => statusOption.value === status,
                      )?.text ?? "Enter your founding year")
                    : "Enter your founding year"}
                </i>
              </div>
            </FormField>
          </GridColumn>
        </Grid>
      </div>
      <Divider hidden />
      <Grid columns={2} mobile={1}>
        <GridColumn>
          <FormField required>
            <FormLabel>Organisation registration number</FormLabel>

            {!creditsafeRegNoSkipped ? (
              <>
                <Input
                  fluid
                  placeholder="01234567"
                  defaultValue={creditsafeRegNo}
                  onChange={onRegNoChange}
                  disabled={creditsafeRegNoSkipped}
                />
                {regIsEditing ? (
                  <IdentifierLookupStatus state={regLookup} />
                ) : (
                  <CompaniesHouseCheckCard businessProfile={businessProfile} />
                )}
              </>
            ) : null}
          </FormField>
          <Checkbox
            checked={creditsafeRegNoSkipped}
            onChange={(_e, { checked }) => {
              updateProfile({ creditsafeRegNoSkipped: checked });
              if (checked) {
                setRegLookup({ status: "idle" });
              }
            }}
            style={{ marginTop: "0.3rem" }}
          >
            My business does not have a registration number
          </Checkbox>
        </GridColumn>
        <GridColumn>
          <FormField required>
            <FormLabel>VAT registration number</FormLabel>

            {!vatNoSkipped ? (
              <>
                <Input
                  fluid
                  placeholder="GB123456789"
                  value={vatNo ?? ""}
                  onChange={onVatNoChange}
                  disabled={vatNoSkipped}
                />
                {vatIsEditing ? (
                  <IdentifierLookupStatus state={vatLookup} />
                ) : (
                  <VatNumberCheckCard businessProfile={businessProfile} />
                )}
                {vatSuggestion && !vatNo ? (
                  <Message color="blue" size="small" style={{ marginTop: 8 }}>
                    <div>
                      {t("ideaBusinessProfiles.lookup.vatSuggestion", {
                        vatNo: vatSuggestion.vatNo,
                        name: vatSuggestion.name,
                      })}
                    </div>
                    <Button
                      size="small"
                      color="primary"
                      style={{ marginTop: 6 }}
                      onClick={applyVatSuggestion}
                    >
                      {t("ideaBusinessProfiles.lookup.vatSuggestionUse")}
                    </Button>
                  </Message>
                ) : null}
              </>
            ) : null}
          </FormField>
          <Checkbox
            checked={vatNoSkipped}
            onChange={(_e, { checked }) => {
              updateProfile({ vatNoSkipped: checked });
              if (checked) {
                setVatLookup({ status: "idle" });
              }
            }}
            style={{ marginTop: "0.3rem" }}
          >
            My business does not have a VAT number
          </Checkbox>
        </GridColumn>
      </Grid>
      <Divider hidden />
      <H4>Accreditations</H4>
      <AccreditationsContainer>
        {profileAccreditations.map((accreditation) => (
          <Label key={accreditation} size="medium">
            <span>
              {accreditationOptions.find((a) => a?.key === accreditation)
                ?.text || accreditation}
            </span>
            <Button
              size="small"
              variant="outline"
              onClick={() =>
                updateProfile({
                  accreditations: profileAccreditations.filter(
                    (a) => a !== accreditation,
                  ),
                })
              }
            >
              <Icon icon={Trash2Icon} />
            </Button>
          </Label>
        ))}
        <Dropdown
          style={{ position: "relative", zIndex: 20 }}
          trigger={
            <Button size="small">
              <Icon icon={PlusIcon} />
              Add accreditation
            </Button>
          }
        >
          <DropdownMenu>
            {accreditationOptions.map((accreditation) => (
              <DropdownItem
                key={accreditation.key}
                onClick={() => {
                  if (profileAccreditations.includes(accreditation.key)) {
                    toast.error("This accreditation is already added");
                    return;
                  }
                  if (accreditation.key === "other") {
                    setAddingCustomAccreditation(true);
                  } else {
                    updateProfile({
                      accreditations: [
                        ...profileAccreditations,
                        accreditation.key,
                      ],
                    });
                  }
                }}
              >
                {accreditation.text}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        {addingCustomAccreditation && (
          <>
            <Input
              fluid
              placeholder="Enter accreditation"
              defaultValue={customAccreditation}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCustomAccreditation(e.target.value)
              }
            />
            <Button
              onClick={() => {
                updateProfile({
                  accreditations: [
                    ...profileAccreditations,
                    customAccreditation,
                  ],
                });
                setCustomAccreditation("");
                setAddingCustomAccreditation(false);
              }}
            >
              Add
            </Button>
          </>
        )}
      </AccreditationsContainer>
      {(tagsQuery.data?.length ?? 0) > 0 && (
        <>
          <H4>
            Tags
            <Subheader>
              {" "}
              <TagModalChooser
                trigger={
                  <Button size="small">
                    <Icon icon={HashIcon} />
                    {t("tags.edit")}
                  </Button>
                }
                tags={ownerTags || []}
                availableTags={
                  tagsQuery.data?.filter(
                    (tag) =>
                      !tag?.disabledEntities?.includes("ideaBusinessProfiles"),
                  ) || []
                }
                canEdit={true}
                addTag={(tag) => {
                  addTagMutation.mutate([
                    "ideaBusinessProfiles",
                    tag._id,
                    businessProfile._id,
                  ]);
                }}
                removeTag={(tag) => {
                  removeTagMutation.mutate([
                    "ideaBusinessProfiles",
                    tag._id,
                    businessProfile._id,
                  ]);
                }}
              />
            </Subheader>
          </H4>
        </>
      )}
    </>
  );
};
